import axios from "axios";
import { Constants } from "../utils/constants";
import {CookieService} from './cookie.service';
import { Utils } from "../utils/utils";

const search = window.location.search;
const params = new URLSearchParams(search);
let ansid = params.get('ANsid');
const AnsidCookie = CookieService.getCookie("ANsid");
if(!ansid && AnsidCookie){
    ansid = AnsidCookie;
  }
export let authansid = ansid;

export const MediaOpsService = {
  campaignSentToMediaOcean: '',

  /**
   * Description The `campaignFilterOptions` function takes in an object `filterObj` as a parameter, which
   * contains filter options for a campaign search. It then maps the keys of `filterObj` to their
   * corresponding values in `mapObj`, which is a mapping of filter options to their API parameter
   * names. It creates a new object `filterParams` and adds only the non-empty filter options to it,
   * using the mapped parameter names as keys. Finally, it returns the `filterParams` object, which
   * can be used as query parameters in an API call to search for campaigns.
   * @param {object} filterObj
   * @returns  {filterParams} which can be used as query parameters in an API call to search for campaigns.
   */
  campaignFilterOptions: (filterObj, parentId) => {
    const modifiedFilter = { ...filterObj };
    modifiedFilter['startDate'] = Utils.convertToYYYYMMDD(filterObj['startDate']);
    modifiedFilter['endDate'] = Utils.convertToYYYYMMDD(filterObj['endDate']);

    let mapObj = {
      "campaignName": "campaign-name",
      "startDate": "start-date",
      "endDate": "end-date",
      "budgetMin": "net-media-min",
      "budgetMax": "net-media-max",
      "status": "export-status",
      "clientName": "client-id",
      "clientCode": "client-code-planit",
      "productCode": "product-code-planit",
      "estimateCode": "estimate-code-planit",
      "orderedNetMediaMax": "order-net-media-max",
      "orderedNetMediaMin": "order-net-media-min",
      "plannedNetMediaMax": "planned-net-media-max",
      "plannedNetMediaMin": "planned-net-media-min"
    };
    let filterParams = {};
    for (let key in modifiedFilter) {
      if (modifiedFilter[key] !== "") {
        filterParams[mapObj[key] || key] = modifiedFilter[key];
      }
    }
    return filterParams;
  },
  
  /**
   * Description The `getCampaignData` function is making an API call to retrieve campaign data based on the
   * provided filter options. It takes in three parameters: `filterObj` which contains filter options
   * for the campaign search, `hasNext` which is a URL for the next page of results (if available),
   * and `isFreshRequest` which is a boolean indicating whether this is a fresh request or not.
   * @param {object} filterObj
   * @param {object} hasNext
   * @param {object} isFreshRequest
   * @returns {object} which is a boolean indicating whether this is a fresh request or not
   */
  getCampaignData: (filterObj, hasNext, isArchive) => {
    let filterParams = {};
    if (filterObj) {
      filterParams = MediaOpsService.campaignFilterOptions(filterObj);
    }
    let queryParam = {}
    if (!hasNext) {
      queryParam = {
        "client-id": Utils.getRouteParam("clientid"),
        "page-no": 1
      }
    }
    for (let filter in filterParams) {
      queryParam[filter] = filterParams[filter];
    }
    queryParam['is-archive'] = isArchive ? isArchive : false; 
    let url;
    if (hasNext) {
      url = hasNext
    } else {
      url = `${Constants.CampaignDataUrl}`;
    }

    return axios.get(url,
      {
        params: { ...queryParam},
        headers: {
          ansid: CookieService.getCookie('ANsid'),
          clientid: Utils.getRouteParam("clientid")
        }
      })
      .then((response) => { return response; },
        (error) => { return Promise.reject(error); });
  },

  /**
   * Retrieves campaign data using AG Grid-specific API endpoint with advanced filtering and pagination support.
   * 
   * This function fetches campaign data from the V3 API endpoint specifically designed for AG Grid integration.
   * It supports comprehensive filtering options, pagination, and archive status filtering. The function handles
   * both initial data loading and subsequent pagination requests seamlessly.
   * 
   * @function getCampaignDataAggrid
   * @param {Object} [filterObj] - Optional filter object containing campaign search criteria.
   * @param {string} [filterObj.campaignName] - Filter by campaign name (partial match supported).
   * @param {string} [filterObj.startDate] - Filter by campaign start date (YYYY-MM-DD format).
   * @param {string} [filterObj.endDate] - Filter by campaign end date (YYYY-MM-DD format).
   * @param {string|number} [filterObj.budgetMin] - Minimum budget filter for net media cost.
   * @param {string|number} [filterObj.budgetMax] - Maximum budget filter for net media cost.
   * @param {string} [filterObj.status] - Filter by export status (e.g., "Synced", "Error", "Not Exported").
   * @param {string} [filterObj.clientName] - Filter by client name or client ID.
   * @param {string} [filterObj.clientCode] - Filter by client code from planning system.
   * @param {string} [filterObj.productCode] - Filter by product code from planning system.
   * @param {string} [filterObj.estimateCode] - Filter by estimate code from planning system.
   * 
   * @param {string} [hasNext] - Complete URL for pagination. When provided, indicates this is a 
   *                            paginated request and the URL contains all necessary parameters
   *                            for fetching the next set of results.
   * 
   * @param {boolean} [isArchive=false] - Flag to determine whether to fetch archived campaigns.
   *                                     - `true`: Fetch only archived campaigns
   *                                     - `false` or `undefined`: Fetch only active campaigns
   */
  getCampaignDataAggrid: (filterObj, hasNext, isArchive, versionOneApi) => {
    let filterParams = {};
    if (filterObj) {
      filterParams = MediaOpsService.campaignFilterOptions(filterObj);
    }
    let queryParam = {}
    if (!hasNext) {
      queryParam = { "client-id": Utils.getRouteParam("clientid"), "page-no": 1 }
    }
    for (let filter in filterParams) {
      queryParam[filter] = filterParams[filter];
    }
    queryParam['is-archive'] = isArchive ? isArchive : false;

    let url;
    if (hasNext) {
      url = hasNext
    } else {
      url = `${Constants.CampaignDataUrl}`;
    }
    
    return axios.get(url,
      {
        params: { ...queryParam },
        headers: {
          ansid: CookieService.getCookie('ANsid'),
          clientid: Utils.getRouteParam("clientid"),
        }
      })
      .then((response) => { return response; },
        (error) => { return Promise.reject(error); });
  },

  /**
  * Description The `sortCampaigns` function is making an API call to retrieve campaign data from
  * `Constants.CampaignDataUrl` with sorting options based on the provided `sortColumn` and
  * `sortOrder` parameters. It creates a `queryParam` object with the `client-id`, `page-no`,
  * `sort-column`, and `sort-order` properties set to the corresponding values. It then makes an API
  * call to `Constants.CampaignDataUrl` with the `queryParam` object as query parameters and the
  * `ansid` header set to the value returned by `CookieService.getCookie("ANsid")`. Finally, it
  *  returns the response from the API call or rejects the promise with an error if the API call fails.
  * @param {object} sortColumn
  * @param {object} sortOrder
  * @returns {object} the response from the API call or rejects the promise with an error if the API call fails
  */
  sortCampaigns: (sortColumn, sortOrder, pageNo, isArchive, versionOneApi = 'V1') => {
    let mapObj = {
      'endDate': 'End Date',
      'startDate': 'Start Date',
      'campaignName': 'Campaign Name',
      'budget': 'Budget',
      'exportStatus': 'Export Status',
      'source': 'Data Source'
    }
    const url = versionOneApi === "V1" ? `${Constants.CampaignDataUrl}` : `${Constants.CampaignDataUrlV2}`;
    
    return axios
      .get(url,
        {
          params: {
            "client-id": Utils.getRouteParam("clientid"),
            "page-no": pageNo || 1,
            "sort-column": mapObj[sortColumn],
            "sort-order": sortOrder,
            "is-archive": isArchive ? isArchive : false
          },
          headers: {
            ansid: CookieService.getCookie("ANsid"),
            clientid: Utils.getRouteParam("clientid")
          }
        })
      .then((response) => { return response; },
        (error) => { return Promise.reject(error); })
  },

  
/**
 * Description The `searchCampaign` function is making an API call to retrieve campaign data based on a search
  * parameter `searchParam`. It creates a `queryParam` object with the `client-id`, `page-no`, and
  * `campaign-name` properties set to the corresponding values. It then makes an API call to
  * `Constants.CampaignDataUrl` with the `queryParam` object as query parameters and the `ansid`
  * header set to the value returned by `CookieService.getCookie('ANsid')`. Finally, it returns the response
  * from the API call or rejects the promise with an error if the API call fails.
  * @param {object} searchParam
  * @returns {object} from the API call or rejects the promise with an error if the API call fails
  */
   searchCampaign: (searchParam, isArchive, versionOneApi = 'V1', searchKey = 'campaign-name', hasNext = '') => {
    // let url = versionOneApi === "V1"? `${Constants.CampaignDataUrl}` : `${Constants.CampaignDataUrlV2}`;
    let url = `${Constants.CampaignDataUrl}`;
    let queryParam = { }
    if(!searchParam){
      url = `${Constants.CampaignDataUrl}`;
    }
    queryParam = {
      [searchKey]: searchParam,
      "is-archive": isArchive ? isArchive : false,
      "page-count":10,
      "source":"planned",
    }

    // keeping code for future reference
    // if (hasNext) {
    //   url = hasNext;
    // } else {
    //   queryParam['page-no'] = 1;
      queryParam['client-id'] = Utils.getRouteParam("clientid");
    // }

    return axios.get(
       url,
        {
          params: {...queryParam},
          headers: {
            ansid: CookieService.getCookie('ANsid'),
            clientid: Utils.getRouteParam("clientid")
          }
        }
      )
      .then((response) => {
        return response;
      }, (error) => {
        return Promise.reject(error);
      });
  },

  updateCampaign: (payload, campaignId, versionOneApi = 'V1') => {
    const url = versionOneApi === "V1" ? `${Constants.CampaignDataUrl}` : `${Constants.CampaignDataUrlV2}`;
    return axios
      .put(
        url + '/' + campaignId,
        payload,
        {
          headers: {
            ansid: CookieService.getCookie('ANsid'),
            clientid: Utils.getRouteParam("clientid")
          }
        }
      )
      .then((response) => { return response; },
        (error) => { return Promise.reject(error); });
  },

  /**
   * Makes API call for sending the campaign channels to media ocean.
   * @param {object} payload will have channels and user email ex: {"channels":["Audio", "Cinema"],"userEmail" : "test@annalect.com"}
   * @param {string} campaignId
   * @param {string} versionOneApi to identify which version of PDMS we are using. if v1 then we using CampaignDataUrl else CampaignDataUrlV2
   * @returns response deom the API
  */
 sendCampaignToMediaOcean: (payload, campaignId,versionOneApi = 'V1') => {
  const url = versionOneApi === "V1" ? `${Constants.CampaignDataUrl}` : `${Constants.CampaignDataUrlV2}`;
    return axios
    .post(url + '/' + campaignId + '/send-to-mo?client-id=' + Utils.getRouteParam("clientid"),  
    payload,
    {headers: {
        ansid: authansid,
        clientid: Utils.getRouteParam("clientid")
      }})
    .then((response) => {
        return response && response['data'];
    })
  },


  /** 
   * The below code is defining a function called `getUserProfile` in JavaScript. This function makes an
   * HTTP GET request to a specific URL (`Constants.GetOmniProfileUrl`) and includes a header with a
   * value obtained from a cookie (`ANsid`). The function uses the axios library to make the request and
   * returns a promise that resolves to the response from the server. 
   */
  getUserProfile: () => {
    return axios
      .get(Constants.GetOmniProfileUrl,
        { headers: { "ansid": CookieService.getCookie('ANsid'), "clientid": Utils.getRouteParam("clientid") } })
      .then((response) => {
        return response;
      })
  },

  /**
   * Description: placement filter api call
   * @param {any} filterObj
   * @returns {any}
   */
   prepareFilterOptions: (filterObj) =>{
    let mapObj = {
        "placementName": "placement-taxonomy",
        "startDate": "flight-start",
        "endDate": "flight-end",
        "status": "export-status",
        "total": "placement-cost",
        "plannedCostMin": "planned-cost-min",
        "plannedCostMax": "planned-cost-max",
        "unitsMin": "unit-amount-min",
        "unitsMax": "unit-amount-max",
        "rateMin": "rate-min",
        "rateMax": "rate-max",
    }
    let firlterParams = {};
    for(let key in filterObj){
      if(filterObj[key] !== ""){
        firlterParams[mapObj[key]] = filterObj[key];
      }
    }
    return firlterParams
  },

  /**
   * Description: get placements api call
   * @param {any} campaignId
   * @param {any} filterObj
   * @param {any} versionOneApi identifiy which version of PDMS we are using ,then accordinly using v1 or v2 API 
   * @returns {any}
   */
    getPlacements: (campaignId,filterObj,versionOneApi = "V1") => {
    let firlterParams = {};
    if(filterObj){
      firlterParams = filterObj;
    }
    const url = versionOneApi === "V1" ? `${Constants.CampaignDataUrl}` : `${Constants.CampaignDataUrlV2}`;  
    return axios
    .get(url + '/' + campaignId + '/placements',
      {
        params: { ...firlterParams },
        headers: {
          ansid: authansid,
          clientid: Utils.getRouteParam("clientid")
        }
      })
    .then((response) => {
      return response;
    })
  },

  // search placements
  searchPlacements: (campaignId, params, versionOneApi = 'V1') => {
    const url = versionOneApi === "V1" ? `${Constants.CampaignDataUrl}` : `${Constants.CampaignDataUrlV2}`;
    return axios
    .get(url + '/' + campaignId + '/placements',
      {
        params: { ...params },
        headers: {
          ansid: CookieService.getCookie("ANsid"),
          clientid: Utils.getRouteParam("clientid")
        }
      })
    .then((response) => {
      return response;
    })
  },

  updatePlacement: (campaignId, placementId, payload, versionOneApi) => {
    const url = versionOneApi === "V1" ? `${Constants.CampaignDataUrl}` : `${Constants.CampaignDataUrlV2}`;
    return axios.put(url + '/' + campaignId + '/placements/' + placementId,payload, {
        headers: {
          ansid: authansid,
          clientid: Utils.getRouteParam("clientid")
        }
      }
    )
    .then((response) => {
      return response;
    })
  },

  // move placements & Un Package
  movePlacement: (campaignId, placementId, payload, versionOneApi) => {
    const url = versionOneApi === "V1" ? `${Constants.CampaignDataUrl}` : `${Constants.CampaignDataUrlV2}`;
    return axios.put(url + '/' + campaignId + '/placements/' + placementId,payload, {
        headers: {
          ansid: authansid,
          clientid: Utils.getRouteParam("clientid")
        }
      }
    )
    .then((response) => {
      return response;
    })
  },

  createPlacement: (campaignId, placementPayload) => {
    return axios
    .post(Constants.CampaignDataUrl + '/' + campaignId + '/placements',
      placementPayload, {headers: {
        ansid: CookieService.getCookie("ANsid"),
        clientid: Utils.getRouteParam("clientid")
      }})
    .then((response) => {
      return response;
    })
  },

  deletePlacement: (campaignId, placementId, payload) => {
    return axios
    .put(Constants.CampaignDataUrl + '/' + campaignId + '/placements/' + placementId, payload, {headers: {
      ansid: authansid,
      clientid: Utils.getRouteParam("clientid")
    }})
    .then((response) => {
      return response;
    })
  },

    /**
   * Makes API call for taxonomy data.
   * @param {string} taxonomyType 'campaign' or 'placement'
   * @returns taxonomy data based on the taxonomyType
   */
  getTaxonomy: (taxonomyType) => {
    return axios
    .get(Constants.taxonomyUrl + '/' +  Utils.getRouteParam("clientid") +'/schema',
      {
        params: {
          type: taxonomyType
        },
        headers: {
          ansid: CookieService.getCookie("ANsid"),
          clientid: Utils.getRouteParam("clientid")
        }
      })
    .then((response) => {
      return response && response.data;
    }, (error) => {
      return Promise.reject(error);
    })
  },

  /**
  * Get the campaign data with campaign id and client id of the campaign.
  * @param {string} campaignId
  * @param {string} clientId
  * @returns {Promise<object>} - A Promise that resolves to the merged campaign data. 
  */  
  getCampaignDataById: (campaignId, clientId, versionOneApi) => {
    const url = versionOneApi === "V1" ? `${Constants.CampaignDataUrl}` : `${Constants.CampaignDataUrlV2}`;
    return axios.get(url + '/' + campaignId,{
      params: { "client-id": Utils.getRouteParam("clientid") },
      headers: { ansid: CookieService.getCookie("ANsid"), clientid: Utils.getRouteParam("clientid") }
    }).then((response) => {
      return response && response.data;
    }, (error) => {
      return Promise.reject(error);
    })
  },

  /**
  * Merges campaigns based on given campaign ID and payload.
  * @param {string} campaignId - The ID of the campaign being merged.
  * @param {object} payload - The data payload for merging the campaigns.
  * @returns {Promise<object>} - A Promise that resolves to the merged campaign data. 
  */
  mergeCampaigns: (campaignId, payload) => {
    return axios
      .post(Constants.CampaignDataUrlV1 + '/' + campaignId + '/map',
        payload,
        {
          headers: {
            ansid: authansid,
            clientid: Utils.getRouteParam("clientid")
          }
        })
      .then((response) => {
        return response && response['data'];
      })
  },

  /**
   * Unmerges a campaign by sending a POST request to the MediaOps API.
   *
   * This function sends a request to unmap a campaign from its parent campaign. It uses the provided
   * `campaignId` and `payload` to perform the operation. The API version can be specified using the
   * `versionOneApi` parameter, which defaults to "V1".
   *
   * @function
   * @param {string} campaignId - The ID of the campaign to be unmapped.
   * @param {Object} payload - The payload containing details about the campaign to be unmapped.
   * @param {string} [versionOneApi="V1"] - The API version to use ("V1" or "V2").
   * @returns {Promise<Object>} - A promise that resolves to the response data from the API.
   *
   */
  unmergeCampaigns: (campaignId, payload, versionOneApi = "V1") => {
    const url = versionOneApi === "V1" ? `${Constants.CampaignDataUrl}` : `${Constants.CampaignDataUrlV2}`;

    return axios
      .post(url + '/' + campaignId + '/unmap',
        payload,
        {
          headers: {
            ansid: authansid,
            clientid: Utils.getRouteParam("clientid")
          }
        })
      .then((response) => {
        return response && response['data'];
      })
  },

  /** 
   * The above code is defining a function called `getTodaysDate` in JavaScript. This function returns
   * the current date and time in the format "YYYY-MM-DD HH:MM:SS.MMM". It uses the `Date` object to get
   * the current date and time, and then formats it into the desired string format using various methods
   * such as `getFullYear()`, `getMonth()`, `getDate()`, `getHours()`, `getMinutes()`, `getSeconds()`,
   * and `getMilliseconds()`. The resulting formatted date string is then returned by the function. 
   */
  getTodaysDate: () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    const hours = String(currentDate.getHours()).padStart(2, '0');
    const minutes = String(currentDate.getMinutes()).padStart(2, '0');
    const seconds = String(currentDate.getSeconds()).padStart(2, '0');
    const milliseconds = String(currentDate.getMilliseconds()).padStart(3, '0');
    const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
    return formattedDate;
  },
  /* The belo code is a JavaScript function called `prepareCampaignData` that takes in a
  `responseData` parameter. It iterates over each object in the `responseData` array and creates a
  new object `dataObj` with modified properties. It assigns default values to certain properties if
  they are not present in the original object. It then adds additional properties to `dataObj` based
  on the original object's properties. Finally, it pushes `dataObj` into a new array called `data`.
  The function returns the `data` array. */
  prepareCampaignData: (responseData) => {
    let data = [];
    for (let index = 0; index < responseData.length; index++) {
      const mediaObj = responseData[index];
      const dataObj = {...mediaObj};
      dataObj['id'] = mediaObj['id'] || '';
      dataObj['startDate'] = mediaObj['start_date'] || '';
      dataObj['endDate'] = mediaObj['end_date'] || '';
      dataObj['campaignName'] = mediaObj['campaign_name'] || '';
      dataObj['campaignDisplayName'] = mediaObj['campaign_display_name'] || '';
      dataObj['partners'] = mediaObj['partners'] || '';
      dataObj['budget'] = mediaObj.media_attributes?.media_budget?.campaign_budget || '';
      dataObj['exportStatus'] = mediaObj['export_status'] || '';
      dataObj['exportStatusError'] = mediaObj['error_description'] || '';
      dataObj['source'] = mediaObj['data_source'] || '';
      dataObj['createdOn'] = mediaObj['created_at'] || '';
      dataObj['createdBy'] = mediaObj['createdBy'] || '';
      dataObj['source'] = mediaObj['data_source'] || '';
      dataObj['clientName'] = mediaObj['client_name'] || '';
      dataObj['importStatus'] = mediaObj['import_status'] || '';
      dataObj['mediaBudget'] = mediaObj.media_attributes?.media_budget || '';
      dataObj['channels'] = mediaObj.media_attributes?.channels || '';
      dataObj['fiscal year'] = mediaObj['fiscal_year'] || '';
      dataObj['campaign period'] = mediaObj['campaign_period'] || '';
      dataObj['line of business/business/brand'] = mediaObj['line_of_business'] || '';
      dataObj['product/product line'] = mediaObj['product_line'] || '';
      dataObj['campaign objective'] = mediaObj['campaign_objective'] || 'Awareness';
      dataObj['country'] = mediaObj['country'] || '';
      dataObj['currency'] = mediaObj['currency'] || '';
      dataObj['market/region'] = mediaObj['region'] || '';
      dataObj['funnel stage'] = mediaObj['funnel stage'] || '';
      dataObj['campaign name/description'] = mediaObj['campaign_name'] || '';
      dataObj['campaignTaxonomy'] = mediaObj['campaign_taxonomy'];
      dataObj['taxonomy_schema'] = mediaObj['taxonomy_schema'];
      dataObj['campaign_taxonomy_display'] = mediaObj['campaign_taxonomy_display'];
      dataObj['_uniqueKey'] = mediaObj["id"] + "-" + index;
      dataObj['synthetic_id'] = mediaObj["synthetic_id"];
      dataObj['parent_id'] = mediaObj["parent_id"];
      dataObj['mediaocean_id'] = mediaObj["mediaocean_id"];
      dataObj['client_code'] = mediaObj["client_code"];
      dataObj['created_at'] = mediaObj["created_at"];
      dataObj['created_by'] = mediaObj["created_by"];
      dataObj['clientid'] = mediaObj["clientid"] || '';
      dataObj['estimateid'] = mediaObj["estimateid"] || '';
      dataObj['productid'] = mediaObj["productid"] || '';
      dataObj['parentSyntheticId'] = mediaObj["parentSyntheticId"] || undefined;
      dataObj['parentStatus'] = mediaObj["parentStatus"] || undefined;
      data.push(dataObj);
    }
    return data;
  },

 
 /**
  * The below code is a JavaScript function called `prepareCampaignDataForClientCode` that takes in a
  `mediaObj` parameter. creates a  new object `dataObj` with modified properties with few selected values
  * @param {object} mediaObj campaign Detail
  * @param {string} taxonomyName campaign taxonomy name
  * @param {string} taxonomyDisplay campaign taxonomy display name
  * @param {string} taxonomySchemay campaign taxonomy schema
  * @returns return object selected values for payload
  */
  prepareCampaignDataForClientCode: (mediaObj,taxonomyName,taxonomyDisplay,taxonomySchemay) =>{
    const dataObj = {};
    
    dataObj['campaignTaxonomy'] = mediaObj['campaign_taxonomy'] ||taxonomyName ;
    //extra login because value of schema can be null or ' ' with space so space count as value in condiation
    dataObj['taxonomySchema'] = mediaObj['taxonomy_schema'] ? (mediaObj['taxonomy_schema'].trim() ||taxonomySchemay) : taxonomySchemay ;
    dataObj['campaignTaxonomyDisplay'] = mediaObj['campaign_taxonomy_display']? (mediaObj['campaign_taxonomy_display'].trim() || taxonomyDisplay): taxonomyDisplay;
    return dataObj;
  },


  /**
     * The above code is a JavaScript function called `getSource` that takes a string as input and returns
     * a new string where each word in the input string is capitalized. It achieves this by splitting the
     * input string into an array of words, capitalizing the first letter of each word, and then joining\
     * the capitalized words back into a string. 
     * @param {string} source
     */
   getSource: (source) => {
    // Split the string into words.
    const words = source.split(" ");
    // Capitalize the first letter of each word.
    const capitalizedWords = words.map(word => {
        const firstLetter = word[0]?.toUpperCase();
        const restOfWord = word.slice(1);
        return firstLetter + restOfWord;
    });
    // Join the capitalized words back into a string.
    return capitalizedWords.join(" ");
  },

    /**
     * Removing last array value if it's null,
     * Converting array list into string with ellips if string have more then 22 characters 
     * @param {Array} values list of channel
     * @returns {String} channelString channel list in string if more then 22 then concatating ellips
     */
     getChannels: (values) => {
      let channel = values?.length && values.map(val=> val.channel_display_name || val.channel_name);
      let channelString = channel.toString();
      return channelString;
  },

  /**
     * Removing  array value if it's null,
     * Converting array list into string  
     * @param {Array} values list of partners
     * @returns {String} partnerString partner list in string
     */
   getPartnerString: (values) => {
    let partners = values?.partners
    partners?.length && partners.forEach((val,index)=>{
        if(val == null){
            partners.splice(index,1) 
        }
    })    
    return partners?.toString();
  },

  /**
   * Get client code from API for Campaign 
   * exmple:https://devmediaops-api.annalect.com/dev/clients/E10B3004-4F8B-11E9-B2C7-12EB69B8A354/clientcodes
   */
  getClientCodes: () => {
    return axios
      .get(Constants.ClientDataUrl+'/'+Utils.getRouteParam("clientid")+'/clientcodes', {
        headers: {
          ansid: CookieService.getCookie('ANsid'),
          clientid: Utils.getRouteParam("clientid")
        }
      })
      .then((response) => {
        return response;
      })
  },

  /**
   * Function use to add three dot (...) end of string
   * if string length is more then given number
   * @param {string} value value in string
   * @param {number} number splice the string using number
   * @returns return string value
   */
  concatenateDotsInString: (value,number) => {
    return value.length <= number ? value : value.substring(0, number).concat('...');
  },

  /**
 * Retrieves the MediaOps client profile for the current client.
 *
 * @function getMediaOpsClientProfile
 * @returns {Promise<Object>} A promise that resolves to the client profile data.
 * @throws {Error} If there is an error while fetching the client profile.
 */
  getMediaOpsClientProfile: () => {
    return axios
      .get(Constants.ClientDataUrl + '/' + Utils.getRouteParam("clientid") + '/mediaopsclientprofile', {
        headers: {
          ansid: CookieService.getCookie('ANsid'),
        }
      })
      .then((response) => {
        return response?.data;
      })
      .catch((error) => {
        return Promise.reject(error);
      }
      )
  },

    /**
     * Retrieves the MediaOps client profile for the current client.
     * @param {string} clientId
     * @returns {Promise<Object>} A promise that resolves to the client profile data.
     */
    getOrphanedDeliveredData: (params = {},hasNext) => {
      let url = Constants.ClientDataUrl + '/' + Utils.getRouteParam("clientid") + '/' + 'taxonomy_values'
      if(hasNext){
        url = hasNext;
        params = {};
      }
      return axios.get(url,
          {
            headers: {
              ansid: CookieService.getCookie("ANsid"),
              clientid: Utils.getRouteParam("clientid")
            },
            params: { ...params },
        }
          ).then((response) => {
              return response;
          })
  },

  /**
   * Map the orphaned delivered data to the placement
   * @param {Object} payload
   * @param {Object} params
   * @returns {Object} response
   */
  mapOrphanedDeliveredData: (payload, params) => {
    return axios.put(Constants.ClientDataUrl + '/' + Utils.getRouteParam("clientid") + '/' + 'taxonomy_values',
    payload,
        {
          headers: {
            ansid: CookieService.getCookie("ANsid"),
            clientid: Utils.getRouteParam("clientid")
          },
          params,
      }
        ).then((response) => {
            return response;
        })
  },

  /**
   * Converts the elements of the given file revision array to numbers and returns the highest number.
   * If the input is not an array, it returns the input as is.
   *
   * @param {Array|string} filerevision - The file revision data, either as an array of strings/numbers or a single string.
   * @returns {number|string} - If the input is an array, it returns the highest number in the array.
   *                                             If the input is a string, it returns the input as is.
   */
  getSubmittedVersion: (filerevision) => {
    if (filerevision && typeof filerevision !== 'string') {
      // Convert strings to numbers and find the highest number
      return Math.max(...filerevision.map(Number));
    } else {
      return filerevision;
    }
  },

  /**
   * Checks if the given object has at least one value that is not an empty object.
   *
   * @param {Object} obj - The object to check.
   * @returns {boolean} - Returns true if at least one value is not an empty object, otherwise false.
   */
  hasNonEmptyValue: (obj) => {    
    if (obj == null) return false; // Check if obj is null or undefined
    return Object.values(obj).some(value => {
      if (typeof value === 'object' && value !== null) {
        return Object.keys(value).length > 0;
      }
      return value !== undefined && value !== '';
    });
  },

  /**
   * Below API call is used to get the platform campaign data.
   * @returns {Promise<Object>} A promise that resolves to the platform campaign data.
   * @throws {Error} If there is an error while fetching the platform campaign data.
   */
  getPlatformCampaign: () => { 
    const clientId = Utils.getRouteParam("clientid")?.toLowerCase();
    return axios.get(Constants.CampaignSyncUrl + `/${clientId}/campaigns/unmapped`,
      {
        headers: {
          ansid: CookieService.getCookie("ANsid"),
          clientid: Utils.getRouteParam("clientid")
        },
      }).then((response) => {
        return response;
      })
  },

  /**
   * Below API call is used to map the platform campaign data.
   * @param {Object} payload - The payload to map the platform campaign.
   * @returns {Promise<Object>} A promise that resolves to the response from the API call. 
   */
  mapPlatformCampaign: (payload) => { 
    const clientId = Utils.getRouteParam("clientid")?.toLowerCase();
    return axios.post(Constants.CampaignSyncUrl + `/${clientId}/campaigns/map_campaigns`,
      payload,
      {
        headers: {
          ansid: CookieService.getCookie("ANsid"),
          clientid: Utils.getRouteParam("clientid")
        },
      }).then((response) => {
        return response;
      })
  },

  /**
   * Checks if the given object has at least one value that is not an empty object or an empty array.
   * @param {Object} obj - The object to check.
   * @returns {boolean} - Returns true if at least one value is not an empty object or an empty array, otherwise false.
   */
  campaignSync: (params = {}) => { 
    const clientId = Utils.getRouteParam("clientid")?.toLowerCase();
    return axios.post(Constants.CampaignSyncUrl + `/${clientId}/sync_campaigns`,
      params,
      {
        headers: {
          ansid: CookieService.getCookie("ANsid"),
          clientid: Utils.getRouteParam("clientid")
        },
      }).then((response) => {
        return response;
      })
  },

  /**
   * Checks if the given object has at least one value that is not an empty object or an empty array.
   * @param {Object} obj - The object to check.
   * @returns {boolean} - Returns true if at least one value is not an empty object or an empty array, otherwise false.
   */
  campaignSyncStatus: (params = {}) => { 
    const clientId = Utils.getRouteParam("clientid")?.toLowerCase();
    return axios.get(Constants.CampaignSyncUrl + `/${clientId}/sync_status?sync_id=${params?.sync_id}`,
      {
        headers: {
          ansid: CookieService.getCookie("ANsid"),
          clientid: Utils.getRouteParam("clientid")
        },
        // params,
      }).then((response) => {
        return response;
      })
  },

  /**
   * Checks if the given object has at least one value that is not an empty object or an empty array.
   * @param {Object} obj - The object to check.
   * @returns {boolean} - Returns true if at least one value is not an empty object or an empty array, otherwise false.
   */
  placementSyncV2: (syntheticId, payload) => {
    const clientId = Utils.getRouteParam("clientid")?.toLowerCase();
    return axios.post(Constants.PlacementSyncUrlV2 + `/${syntheticId}/placements/sync?client-id=${clientId}&type=placement`,
    payload,
    {
      headers: {
        ansid: CookieService.getCookie("ANsid"),
        clientid: Utils.getRouteParam("clientid")
      },
  }).then((response) => {
      return response;
    })
  },

  createCampaignNestedData: (campaigns, isTableNested, setIsTableNested) => {
    const nestedData = {};
    for (const item of campaigns) {
      if (item.parent_id) {
        const parentId = item.parent_id;
        if (!(parentId in nestedData)) {
          nestedData[parentId] = [];
        }
        nestedData[parentId].push(item);
      }
    }
    // Checking campaigns for the nested data and adding it to the nestedData object
    let transformedFilterData = [];
    let nestedCampaigns = [];
    // runnging loop on parent campaign only
    for (const item of campaigns) {
      if (!(item.parent_id in nestedData) || !(item.synthetic_id in nestedData)) {
        //check if parent have child campaign in nestedData
        let childCampaign = nestedData[item.id] || nestedData[item.synthetic_id]
        if (childCampaign?.length) {
          if(!(item?.nestedData?.length)){
            item.nestedData = []
          }

          childCampaign.forEach(val=>{
            val.local_currency_name = item.local_currency_name;
            val['isCheckBox'] = false;
            val['marginClass'] = "margin-left-20";
            val['nested'] = true;
            val.partners = val?.channels?.reduce((acc, channel) => {
              const publishersName = [];
              channel?.publishers?.map((publisher) => {
                if(!acc.includes(publisher?.publisher_name)){
                  publishersName.push(publisher?.publisher_name)
                }
              })
              return  acc.concat(publishersName)
            }, []); 
          });

          item.nestedData.push(...childCampaign); 
          nestedCampaigns = nestedCampaigns.concat(childCampaign);
          if (!isTableNested) {
            setIsTableNested(true)
          }
          //make every nested true if it have child
          if (!item?.nested){
            item['nested'] = true;
            item?.nestedData.forEach((nestedItem) => {
              nestedItem['nested'] = true;
            });
          }
        }
        transformedFilterData.push(item);
        
      }
    }
    
    // The below code is to filter the `transformedData` to remove any duplicate campaigns that is already present as child campaign
    let duplicatedCampaign = new Set(nestedCampaigns.map(({ synthetic_id }) => synthetic_id));
    transformedFilterData = transformedFilterData.filter(({ synthetic_id }) => !duplicatedCampaign.has(synthetic_id));

    const fetchCount = MediaOpsService.fetchCount(transformedFilterData, false);
    return {transformedFilterData, fetchCount};
  },

  fetchCount: (campaigns, searchEnabled = false) => {
    if (searchEnabled) {
      // Calculate total count including child campaigns using functional approach
      const childCampaignsCount = campaigns.reduce((totalChildCount, campaign) => {
        return totalChildCount + (campaign?.children?.length || 0);
      }, 0);

      return { fetchCount: campaigns.length + childCampaignsCount };
    } else {
      let childCampaignsCount = 0;
      let transformedFilterData = campaigns.map(item => {
        if (item.nestedData && Array.isArray(item.nestedData)) {
          for (const nestedItem of item?.nestedData) {
            childCampaignsCount++;
          }
        }
        return item;
      });
      const fetchCount = transformedFilterData.length + childCampaignsCount;
      return { fetchCount };
    }
  },

  getStatusColor: (status) => {
    const errorStatus = ["needs approval", "sync error", "rejected", "synthetic id missing", "taxonomy error", "updated", "needs update",];
    const commonStatus = ["not approved", "not exported", "sent to seller", "not synced",];

    if (!status) return "#1a8978"; // Default color

    const lowerCaseStatus = status.toLowerCase();

    if (commonStatus.includes(lowerCaseStatus)) {
      return "#8b8d90"; // Gray for common statuses
    } else if (
      errorStatus.includes(lowerCaseStatus) ||
      lowerCaseStatus.includes("error")
    ) {
      return "#9b373c"; // Red for error statuses
    }

    return "#1a8978"; // Default color
  },

  sortCampaignsAggrid: (sortColumn, sortOrder, isArchive, versionOneApi = 'V1', hasNext) => {
    let mapObj = {
      'source': 'Data Source',
      'campaignName': 'Campaign Name',
      'endDate': 'End Date',
      'startDate': 'Start Date',
      'mediaocean_status': 'Export Status',
      // 'budget': 'Budget',
    }

    const paramsObj = {
      "client-id": CookieService.getCookie("clientId"),
      "sort-column": mapObj[sortColumn],
      "sort-order": sortOrder,
      "is-archive": isArchive ? isArchive : false
    }

    let url;
    if (hasNext) {
      url = hasNext
    } else {
      url = versionOneApi === "V1" ? `${Constants.CampaignDataUrl}` : `${Constants.CampaignDataUrlV2}`;
      paramsObj['page-no'] = 1;
    }

    return axios
      .get(url,
        {
          params: paramsObj,
          headers: {
            ansid: CookieService.getCookie("ANsid"),
            clientid: CookieService.getCookie("clientId")
          }
        })
      .then((response) => { return response; },
        (error) => { return Promise.reject(error); })
  },

  searchCampaignsAggrid: (searchParam, isArchive, versionOneApi = 'V1', hasNext) => {
    let paramsObj = {}
    paramsObj = {
      "client-id": CookieService.getCookie("clientId"),
      "search": searchParam,
      "is-archive": isArchive ? isArchive : false,
    }

    let url;
    if (hasNext) {
      url = hasNext
    } else {
      url = versionOneApi === "V1" ? `${Constants.CampaignDataUrl}` : `${Constants.CampaignDataUrlV2}`;
      paramsObj['page-no'] = 1;
    }

    return axios.get(
      url,
      {
        params: { ...paramsObj },
        headers: {
          ansid: CookieService.getCookie('ANsid'),
          clientid: CookieService.getCookie("clientId")
        }
      }
    )
      .then((response) => {
        return response;
      }, (error) => {
        return Promise.reject(error);
      });
  },

  /**
   * Transforms campaign data into a nested structure for AG Grid.
   *
   * This function processes a flat array of campaign data and organizes it into a hierarchical structure
   * based on parent-child relationships. It uses `id` and `synthetic_id` to identify and link parent and child campaigns.
   * If a parent campaign is missing, the child campaign is added to the result as a top-level item.
   * The final result is sorted by the original index to maintain the order of the input data.
   *
   * @function
   * @param {Array<Object>} data - The flat array of campaign data to be transformed.
   * @returns {Array<Object>} - A nested array of campaign data, with parent-child relationships established.
   */
  transformDataAggrid: (data) => {
    const map = new Map();
    const mapWithSynthetic = new Map();
    const result = [];

    // First, index everything by amd_campaign_id & id
    data.forEach((item, index) => {
      if (item?.amd_campaign_id) {
        map.set(item?.amd_campaign_id, { ...item, originalIndex: index });
      }
      if(item?.synthetic_id) {
        mapWithSynthetic.set(item?.synthetic_id, { ...item});
      }
    });

    // Then assign children to parents
    data.forEach((item) => {
      if (item?.parent_id != null) {
        let parent = null;
        if(item.data_source?.toLowerCase() === 'mediaocean'){
          let parentWithId = mapWithSynthetic.get(item?.parent_id); // Check by parent_synthetic_id
          parent = map.get(parentWithId?.amd_campaign_id); // Check by amd_campaign_id since nested data is updated in this
        } else {
          parent = map.get(item?.parent_id); // Check by parent_id
        }
        if (parent) {
          parent.nestedData = parent.nested ? parent.nestedData : [];
          // set child item with parent data to be used
          item.local_currency_name = parent.local_currency_name;
          item.parent_amd_campaign_id = parent?.amd_campaign_id;
          item.parentSyntheticId = parent?.synthetic_id;
          item.parentStatus = parent?.export_status;
          parent.nestedData.push(item); // Add child to parent's nestedData
          parent['nested'] = true;
        }
      }
    });

    // Finally, return only top-level items (those without parent_id)
    map.forEach((item) => {
      if (!item?.parent_id) {
        {
          // If the item has child_count and is not nested, create a nestedData array with a placeholder
          if(item.child_count && !item.nested){
            item.nestedData = [{
              campaignName: '',
              id: item.id + "child"
            }];
          }
        }
        result.push(item);
      }
    });

    // Sort the result by the original index to maintain the order
    result.sort((a, b) => a.originalIndex - b.originalIndex);
    return result;
  },
  
  setCampaignsData: (campaigns, isTableNested, setIsTableNested, childCampaigns, parentId, searchEnabled = false) => {
    if(childCampaigns.length) {
      let index = campaigns.findIndex(item => item.amd_campaign_id  === parentId || item.synthetic_id  === parentId);
      // Check if the parentId exists in the campaigns array
      // If it does, add the childCampaigns to the nestedData property of the parent campaign
      if (index !== -1) {
        childCampaigns.forEach((nestedItem) => {
          nestedItem['isCheckBox'] = false;
          nestedItem['marginClass'] = "margin-left-20";
          nestedItem['nested'] = true;
          nestedItem.local_currency_name = campaigns[index].local_currency_name;
          nestedItem.parent_amd_campaign_id = campaigns[index]?.amd_campaign_id;
          nestedItem.parentSyntheticId = campaigns[index]?.synthetic_id;
          nestedItem.parentStatus = campaigns[index]?.export_status;
          nestedItem.partners = nestedItem?.channels?.reduce((acc, channel) => {
              const publishersName = [];
              channel?.publishers?.map((publisher) => {
                if(!acc.includes(publisher?.publisher_name)){
                  publishersName.push(publisher?.publisher_name)
                }
              })
              return  acc.concat(publishersName)
            }, []); 
        });
        campaigns[index].nestedData = childCampaigns;
        campaigns[index].nested = true;
        campaigns[index].isCheckBox = true;
        if (!isTableNested) {
            setIsTableNested(true)
        }
      }
    }
    
    const {fetchCount} = MediaOpsService.fetchCount(campaigns, searchEnabled);
    return {transformedFilterData: campaigns, fetchCount};
  }
};
